import {
  courseService,
  surveyService,
  userService,
} from '@jobseeker/common-core/services';
import {
  Card as MuiCard,
  styled,
  TypographyProps,
  Typography as Typo,
  TooltipProps,
  Tooltip,
  tooltipClasses,
  Paper,
  useTheme,
  Dialog,
  Box,
  Button,
  SxProps,
  Theme,
} from '@mui/material';
import type {
  AppState,
  Course,
  DownloadBrochureApiResponse,
} from '@jobseeker/common-core/types';
import SimilarCourse from './../SimilarCourse';
import {HiristLoader} from '@jobseeker/lotus/elements';

import {ImageProps} from 'next/image';
import {Image} from '@jobseeker/lotus/components/Image';
import {useRouter} from 'next/router';
import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {cleanUrl} from '@jobseeker/lotus/utils';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {
  BreifCase,
  CrossIcon,
  IndianRupee,
  OpenBook,
} from '@jobseeker/lotus/assets/icons';
import {
  getQueryParamsAndSeekerId,
  getUbaPageName,
  handleTracking,
  openUrl,
  TrackEventUBA,
} from '@jobseeker/common-core/utils';
import {categories} from '../../constants';
import ProfileInfoPopup from '@jobseeker/feature-course/components/ProfileInfoPopup';
import {EnquiryType} from '@jobseeker/feature-course/components/CourseDetail/EnquiryButtons';
import SurveyPopup from '../SurveyPopup';
import type {QuestionType} from '@jobseeker/lotus/components/SurveyQuestion/types';
import type {Survey as SurveyType} from '@jobseeker/common-core/services/types';

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));
interface ImageWithFallbackProps extends ImageProps {
  fallback: any;
  itemId: any;
}
const Dot = styled('span')(({theme}) => ({
  width: 4,
  height: 4,
  backgroundColor: '#cecece',
  borderRadius: '50%',
  display: 'inline-block',
  marginRight: 14,
  marginLeft: 15,
  verticalAlign: 'middle',
  [theme.breakpoints.down('sm')]: {
    marginRight: 0,
    marginLeft: 0,
  },
}));

const Typography = styled(Typo)<any>((props: TypographyProps) => ({
  display: 'flex',
  alignItems: 'center',
}));
const TooltipHirist = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  ul: {
    margin: 0,
    padding: '14px 15px',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: '#1c2d41',
    color: '#fff',
    fontSize: 16,
    // border: '1px solid #dadde9',
  },
  '.MuiTooltip-arrow:before': {
    backgroundColor: '#1c2d41',
  },
}));

const JoblistPaper = styled(Paper)<any>((props: any) => ({
  borderRadius: 0,
  position: 'relative',
  display: 'flex',
  gap: props.theme.spacing(1.5),
  // cursor: 'pointer',
  '.joblist__logo': {
    borderRadius: props.theme.spacing(1.5),
    border: '0.6px solid #7E7E7E33',
  },
  '.joblist__right_element': {
    position: 'absolute',
    width: 'fit-content',
    right: props.theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    // padding: props.theme.spacing(2),
    top: 0,
    bottom: 0,
  },
  '.joblist__subtitle': {
    p: {
      wordBreak: 'break-all',
      color: props.theme.palette.text.secondary,
    },
  },
  '.save-icon': {
    display: 'none',
  },
  ':hover': {
    '.save-icon': {
      display: 'unset',
    },
  },
}));
const StyledImage = styled(Image)({
  backgroundColor: '#fff',
  border: '1px solid #ececec',
  borderRadius: 10,
  transition: 'box-shadow 0.3s ease',
  '&:hover': {
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
  },
});

export const HiristCourseTile = (e: {
  data: Course;
  handleEnquiryInLoggedOutState?: () => void;
  sx?: SxProps<Theme>;
  forUnpublishedCourses?: boolean;
}) => {
  const {
    data,
    handleEnquiryInLoggedOutState = () => {},
    sx = {},
    forUnpublishedCourses = false,
  } = e;
  const theme = useTheme();
  const useUserAgent = () => {
    const [userAgent, setUserAgent] = useState<string>('');

    useEffect(() => {
      if (typeof navigator !== 'undefined') {
        setUserAgent(navigator.userAgent || navigator.vendor || '');
      }
    }, []);

    return userAgent;
  };
  const userAgent = useUserAgent();
  const token = useSelector((state: AppState) => state.auth.authInfo.token);
  const getDeviceOs = (userAgent: string): string => {
    const isMobile =
      /(iPad|iPhone|Android|webOS|iPod|BlackBerry|IEMobile|Opera Mini|Mobile)/i.test(
        userAgent
      ) || false;

    if (isMobile) {
      if (/android/i.test(userAgent)) {
        return 'android';
      }

      if (/iphone|ipad|ipod|iPhone|ios/.test(userAgent)) {
        return 'ios';
      }
    }

    return 'web';
  };
  const router = useRouter();
  const {userId} = useSelector((state: AppState) => state.auth);
  const {refinv = 'NA', reforg = 'NA'} = router.query;
  const getCategoryIdFromName = (categoryName: any) => {
    const cleanCategoryName = categoryName.replace(/-jobs$/, '');

    const formattedCategoryName = cleanCategoryName
      .split('-')
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const category = categories.find(
      (cat: any) =>
        cat.name.toLowerCase() === formattedCategoryName.toLowerCase()
    );

    return category ? category.id : '';
  };
  const getTagIdFromName = (tagName: any) => {
    const cleanTagName = tagName.replace(/-jobs$/, '');

    const formattedTagName = cleanTagName
      .split('-')
      .map((word: any) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    for (const category of categories) {
      const matchingTag = category.tags.find(
        (tag: any) => tag.name.toLowerCase() === formattedTagName.toLowerCase()
      );
      if (matchingTag) {
        return {
          tagId: matchingTag.id,
          catId: category.id,
        };
      }
    }
    return {tagId: '', catId: ''};
  };
  const getIdsFromUrl = () => {
    const pathname = window.location.pathname;
    let catId = '',
      tagId = '';

    if (pathname.includes('/k/')) {
      const tagMatch = pathname.match(/\/k\/([^.]+)\.html/);
      if (tagMatch) {
        const tagName = tagMatch[1];
        const ids = getTagIdFromName(tagName);
        tagId = String(ids.tagId);
        catId = String(ids.catId);
      }
    } else if (pathname.includes('/c/')) {
      const catMatch = pathname.match(/\/c\/([^.]+)\.html/);
      if (catMatch) {
        const categoryName = catMatch[1];
        catId = String(getCategoryIdFromName(categoryName));
      }
    }

    return {catId, tagId};
  };
  const [load, setLoad] = useState<boolean>(false);
  const [loadin, setLoadin] = useState<boolean>(false);

  const downloadBrochure = async () => {
    setLoad(true);
    handleTracking('lgClickCourseDownload', {
      event_category: 'Learn and Grow',
      CourseId: `${data.id}`,
      UserId: `${userId}`,
      Status: `${userId ? 'LoggedIn' : 'LoggedOut'}`,
      Device: `${getDeviceOs(userAgent)}`,
      Reforg: `${reforg ? reforg : ''}`,
      RefInv: `${refinv ? refinv : ''}`,
    });

    const downloadBrochureApiResponse: DownloadBrochureApiResponse =
      await courseService.downloadBrochure({
        coursePublishedId: data.id,
        refinv: refinv.toString() ?? 'NA',
        reforg: reforg.toString() ?? 'NA',
      });
    setLoad(false);
    if (downloadBrochureApiResponse?.brochure_file) {
      if (data.surveyEnabled && !surveyAnswered) {
        // Fetch survey question
        await fetchSurveyDetails();
        setShowSurvey(true);
        setBrochureFile(downloadBrochureApiResponse.brochure_file);
        return;
      }
      openUrl(downloadBrochureApiResponse.brochure_file, '_blank');
    }
  };
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const [showEnquiryPopup, setShowEnquiryPopup] = useState(false);
  const [profileInfoUpdated, setProfileInfoUpdated] = useState(false);
  const [enquiryType, setEnquiryType] = useState<EnquiryType>(
    EnquiryType.Enquire
  );
  const [countriesJson, setCountriesJson] = useState([
    {countryCode: '', countryName: '', countryCodeAlpha: ''},
  ]);
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveyAnswered, setSurveyAnswered] = useState(false);
  const [surveyQuestions, setSurveyQuestions] = useState<QuestionType[]>([]);
  const [brochureFile, setBrochureFile] = useState<string>('');
  const IsLarge = useIsLargeDevice();
  const {id = 0, enq: enquiryId = -1} = router.query;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  const dispatch =
    /* eslint-disable @typescript-eslint/no-explicit-any */ useDispatch<any>();
  const [eq, seteq] = useState<string>('');
  const enquireCourse = async () => {
    setLoadin(true);
    const response = await courseService.enquireCourse({
      coursePublishedId: Number(data.id),
      refinv: refinv.toString(),
      reforg: reforg.toString(),
    });

    if (response?.error) {
      throw new Error(response.error.message);
    }
    setLoadin(false);
    if (
      response.enquiryID > 0 ||
      (response.blacklistedUser && response.blacklistedUser > 0)
    ) {
      if (data.surveyEnabled && !surveyAnswered) {
        // Fetch survey question
        await fetchSurveyDetails();
        setShowSurvey(true);
        return;
      }
      setdialogOpen(true);
    } else {
      seteq(response.CourseMessage);
    }
  };
  const {experienceYear = -1, phone = '0'} = useSelector(
    (state: AppState) => state.user.profile
  );
  const userLocation =
    useSelector(
      (state: AppState) => state?.user?.profile?.locations?.current?.id
    ) ?? -1;
  const {companyId, companyName, description, location, jobCount} =
    data.showcaseData ? data.showcaseData : data;

  useEffect(() => {
    const handler = () => {
      setOpen(false);
    };
    window?.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  useEffect(() => {
    if (!profileInfoUpdated) {
      return;
    }
    setShowEnquiryPopup(false);
    if (enquiryType === EnquiryType.Enquire) {
      enquireCourse();
    } else if (enquiryType === EnquiryType.DownloadBrochure) {
      downloadBrochure();
    }
  }, [profileInfoUpdated]);

  const fetchSurveyDetails = async () => {
    const survey: SurveyType[] = await surveyService.getSurvey(
      `course-${data.id}`
    );

    if (!survey || survey.length === 0) {
      setSurveyAnswered(true);
      return;
    }

    setSurveyQuestions(survey[0].questions);
    setSurveyAnswered(false); // Important: Set to false when we have questions
  };

  const fetchCountries = async () => {
    try {
      const response = await userService.getS3Resource('countrycodes');
      setCountriesJson(response.countrycodes);
    } catch (error) {
      console.error('Failed to fetch country data', error);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };

  const MainComponent = Card;
  const [hover, setHover] = useState(false);

  const getBackgroundColor = (hover: any) =>
    hover ? theme.palette.primary.main : 'transparent';

  const isLarge = useIsLargeDevice();

  const [dialogOpen, setdialogOpen] = useState<boolean>(false);

  const closeEnquiryPopup = () => {
    setShowEnquiryPopup(false);
  };

  const handleProfileUpdate = () => {
    setProfileInfoUpdated(true);
  };

  const handleSurveyCompletion = () => {
    setSurveyAnswered(true);
    setShowSurvey(false);
    setLoadin(false);

    if (enquiryType === EnquiryType.Enquire) {
      setdialogOpen(true);
    } else if (enquiryType === EnquiryType.DownloadBrochure) {
      openUrl(brochureFile, '_blank');
    }
  };

  return (
    <MainComponent
      data-testid="course-tile-container"
      className="joblist-card-v2"
      color="gray.dark"
      sx={{
        mb: '20px',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'unset',
        transition: 'all ease 0.2s',
        width: {xxs: 'calc(100% - 54px)', md: '92%'},
        ml: {xxs: 4, md: 4},
        ':hover': {
          boxShadow: 'rgba(0, 0, 0, 0.09) 0px 5px 15px 0px;',
        },
        ...sx,
      }}
      onClick={() => {
        handleTracking('lgClickCourse', {
          event_category: 'Learn and Grow',
          CategoryId: getIdsFromUrl().catId.toString(),
          UserId: sessionStorage.getItem('userId') || 'NA',
          Status: sessionStorage.getItem('status') || 'LoggedOut',
          Device: sessionStorage.getItem('device') || 'desktop',
          Reforg: reforg.toString(),
          Refinv: refinv.toString(),
        });
      }}
    >
      {showSurvey && (
        <SurveyPopup
          questions={surveyQuestions}
          setShowSurvey={setShowSurvey}
          successCallback={handleSurveyCompletion}
          courseTitle={data.title}
          instituteName={data.institute}
          courseLogoPath={data.path}
          coursePublishedId={data.id}
        />
      )}
      <a
        data-testid="course-tile-link"
        href={
          forUnpublishedCourses
            ? `/course_unpublished_detail?cId=${data.id}`
            : `/course/${cleanUrl(data.title)}-${data.id}?ref=nav`
        }
        target="_blank"
        style={{
          textDecoration: 'none',
          color: '#000',
        }}
      >
        {showEnquiryPopup && (
          <ProfileInfoPopup
            userId={parseInt(userId, 10)}
            closeEnquiryPopup={closeEnquiryPopup}
            handleProfileUpdate={handleProfileUpdate}
            countriesJson={countriesJson}
          />
        )}
        {token ? (
          <Dialog
            open={dialogOpen}
            onClose={() => {
              setdialogOpen(false);
            }}
            fullWidth
            sx={{
              '.MuiDialog-paper': {
                maxWidth: 900,
                minHeight: 120,
              },
            }}
          >
            <Box display={'flex'} justifyContent={'flex-end'}>
              <Box
                onClick={() => {
                  setdialogOpen(false);
                }}
                sx={{
                  cursor: 'pointer',
                  position: 'relative',
                  width: '50px',
                  height: '50px',
                  backgroundColor: '#f2f5fa',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: 1,
                  '&:hover': {
                    backgroundColor: 'white',
                    boxShadow: '0 6px 14px 0 rgba(0, 0, 0, 0.09)',
                  },
                }}
              >
                <Image
                  src={CrossIcon}
                  alt="cross-icon"
                  height={12}
                  width={12}
                />
              </Box>
            </Box>
            <SimilarCourse
              userId={userId}
              enquiredCourseId={data.id}
              refinv={refinv.toString()}
              reforg={reforg.toString()}
              enquiryId={parseInt(enquiryId.toString(), 10)}
              userExp={experienceYear}
              userLocation={userLocation}
              setdialogOpen={setdialogOpen}
            />
          </Dialog>
        ) : (
          <></>
        )}
        <JoblistPaper
          data-testid="course-tile-content"
          elevation={0}
          sx={{
            py: {xxs: 1.25},
            borderRadius: 2,
            cursor: 'pointer',
            pl: 5,
            pr: 0,
          }}
        >
          <Box
            data-testid="course-tile-image-container"
            sx={{
              display: 'flex',
              position: 'absolute',
              left: '-25px',
              top: theme.spacing(1.25),
            }}
          >
            <StyledImage
              data-testid="course-tile-image"
              src={data.path}
              alt="alt"
              width={50}
              height={50}
              unoptimized
            />
          </Box>
          <Box
            data-testid="course-tile-details"
            display={'flex'}
            width={'100%'}
            onClick={() => {
              handleTracking('lgClickCourse', {
                event_category: 'Learn and Grow',
                CourseId: `${data.id}`,
                UserId: `${userId}`,
                Status: `${userId ? 'LoggedIn' : 'LoggedOut'}`,
                Device: `${getDeviceOs(userAgent)}`,
                Reforg: `${reforg ? reforg : ''}`,
                RefInv: `${refinv ? refinv : ''}`,
              });
              const {queryParams} = getQueryParamsAndSeekerId();
              const {catId, tagId} = getIdsFromUrl();
              TrackEventUBA('courseClick', 'click', {
                ref: queryParams.ref || '',
                page: getUbaPageName(),
                categoryID: catId || '',
                tagID: tagId || '',
                refOrg: reforg || '',
                courseId: id.toString(),
                refInv: refinv || '',
                sessionUsername: userId ? userId.toString() : '',
              });
            }}
          >
            <Box
              sx={{
                // display: 'flex',
                // flexDirection: 'column',
                // justifyContent: 'space-between',
                width: {xxs: '100%', md: '65%', lg: '70%', xxl: '75%'},
              }}
            >
              <Box>
                <Typography
                  data-testid="course-tile-title"
                  variant="subtitle2"
                  fontSize={{xxs: 14, md: 16}}
                  lineHeight="21px"
                  fontWeight={600}
                  width={'90%'}
                  sx={{
                    lineClamp: '3',
                    '-webkit-line-clamp': '2',
                    '-webkit-box-orient': 'vertical',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    display: 'block',
                    alignItems: 'center',
                  }}
                >
                  {data.title}
                </Typography>
                {/* {!data.showcaseData ? (
                  <RatingDiv>
                    {aggregateRatingOneDecimal &&
                      aggregateRatingOneDecimal != '0.0' && (
                        <ImageStar className="icon-save" />
                      )}
                    {aggregateRatingOneDecimal &&
                      aggregateRatingOneDecimal != '0.0' && (
                        <ABRatingDiv>{aggregateRatingOneDecimal}</ABRatingDiv>
                      )}
                    {reviewsCount && reviewsCount != '0' && <RatingSeparator />}
                    {reviewsCount && reviewsCount != '0' && (
                      <ABReviewsA
                        title={`${companyName} Reviews`}
                        target="_blank"
                        rel="nofollow"
                        href={addUtmParamsToUrl(url, 'RATING_REVIEWS')}
                      >{`${reviewsCount} ${
                        reviewsCount == '1' ? 'Review' : 'Reviews'
                      }`}</ABReviewsA>
                    )}
                  </RatingDiv>
                ) : (
                  <></>
                )} */}
              </Box>
              <Box
                data-testid="course-tile-subtitle"
                className="joblist__subtitle"
                sx={{
                  display: 'flex',
                  gap: 1, // Combines rowGap and columnGap
                  alignItems: 'center',
                  color: theme.palette.text.secondary,
                  flexWrap: 'wrap', // Allows items to wrap to the next line if necessary
                  mb: '10px',
                  mt: '5px',
                  mr: {xxs: 1, md: 0},
                }}
              >
                <Typography
                  data-testid="course-tile-institute"
                  variant="subtitle2"
                  fontSize={{xxs: 14, md: 14}}
                  lineHeight="21px"
                  fontWeight={500}
                  sx={{
                    opacity: 0.8,
                    overflow: 'hidden',
                    display: 'block',
                    flex: '0 1 auto', // Takes only required width
                  }}
                >
                  {data.institute}
                </Typography>

                <Dot sx={{flexShrink: 0}} />

                <TooltipHirist
                  data-testid="course-tile-location"
                  arrow
                  id="display_location"
                  className="set-tooltip"
                  title={
                    <Box
                      component="ul"
                      sx={{listStyleType: 'square', paddingLeft: 2}}
                    >
                      {location?.map((info: any, index: number) => (
                        <Box
                          component="li"
                          fontSize={16}
                          fontWeight="normal"
                          key={index}
                          padding="2px 1px"
                        >
                          {info.name}
                        </Box>
                      ))}
                    </Box>
                  }
                >
                  <Typography
                    variant="subtitle2"
                    fontSize={{xxs: 14, md: 14}}
                    lineHeight="21px"
                    fontWeight={500}
                    sx={{
                      opacity: 0.8,
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                      wordWrap: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      flex: '0 1 auto', // Takes only required width
                    }}
                  >
                    {location.length > 1
                      ? 'Multiple Locations'
                      : location[0]?.name}
                  </Typography>
                </TooltipHirist>

                <Dot sx={{flexShrink: 0}} />

                <Typography
                  data-testid="course-tile-duration"
                  sx={{
                    fontWeight: 400,
                    fontSize: 14,
                    opacity: 0.8,
                    flex: '0 1 auto', // Takes only required width
                  }}
                >
                  {data.duration}
                </Typography>
              </Box>

              {data.oneLiner ? (
                <Typography
                  sx={{
                    fontWeight: 400,
                    fontSize: 14,
                    opacity: 0.8,
                    display: {xxs: 'none', md: 'inline-block'},
                  }}
                >
                  {data.oneLiner}
                </Typography>
              ) : (
                <></>
              )}
              {!forUnpublishedCourses && (
                <Box
                  data-testid="course-tile-actions"
                  display={{xxs: 'flex'}}
                  sx={{
                    mt: 2,
                    flexDirection: {xxs: 'column', md: 'row'},
                    ml: {xxs: '0px', md: 0},
                  }}
                >
                  <Button
                    data-testid="course-tile-enquire-btn"
                    onClick={e => {
                      e.preventDefault();

                      e.stopPropagation(); // Prevent the parent onClick from being triggered

                      setEnquiryType(EnquiryType.Enquire);

                      handleTracking('lgClickCourseLead', {
                        event_category: 'Learn and Grow',
                        CourseId: `${data.id}`,
                        UserId: `${userId}`,
                        Status: `${userId ? 'LoggedIn' : 'LoggedOut'}`,
                        Device: `${getDeviceOs(userAgent)}`,
                        Reforg: `${reforg ? reforg : ''}`,
                        RefInv: `${refinv ? refinv : ''}`,
                      });
                      if (!token) {
                        dispatch({
                          type: 'AppContext/set_login_popup',
                          payload: true,
                        });

                        handleEnquiryInLoggedOutState?.();
                      } else {
                        if (
                          token &&
                          !profileInfoUpdated &&
                          (phone === '0' || userLocation === -1)
                        ) {
                          fetchCountries().then(() =>
                            setShowEnquiryPopup(true)
                          );
                          return;
                        }
                        if (token) {
                          enquireCourse();
                        }
                      }
                    }}
                    sx={{
                      border: `1px solid ${theme.palette.primary.main}`,
                      borderRadius: '16px',
                      padding: '5px',
                      px: '20px',
                      width: '168px',
                      height: '33px',
                      display: 'flex',
                      alignItems: 'center',
                      transition: 'background-color 0.3s ease-in-out',
                      '&:hover': {
                        backgroundColor: theme.palette.primary.main,
                        color: '#fff !important',
                        '& img': {
                          filter: 'brightness(0) invert(1)', // Turns image white
                        },
                        '& .text': {
                          color: '#fff', // Changes Typography color
                        },
                      },
                    }}
                    onMouseEnter={() => setHover(true)}
                    onMouseLeave={() => setHover(false)}
                  >
                    <Typography
                      className="text" // Assigning class for better targeting
                      sx={{
                        fontFamily: 'Nunito Sans',
                        fontSize: 14,
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        marginLeft: 1,
                        transition: 'color 0.3s ease-in-out',
                      }}
                    >
                      {!loadin
                        ? 'I am Interested'
                        : HiristLoader(getBackgroundColor(hover))}
                    </Typography>
                  </Button>
                  {data.brochure ? (
                    <Button
                      data-testid="course-tile-brochure-btn"
                      onClick={e => {
                        e.preventDefault();
                        e.stopPropagation(); // Prevent the parent onClick from being triggered
                        setEnquiryType(EnquiryType.DownloadBrochure);
                        if (!token) {
                          dispatch({
                            type: 'AppContext/set_login_popup',
                            payload: true,
                          });
                          handleEnquiryInLoggedOutState?.();
                        }
                        if (
                          token &&
                          !profileInfoUpdated &&
                          (phone === '0' ||
                            experienceYear === -1 ||
                            userLocation === -1)
                        ) {
                          fetchCountries().then(() =>
                            setShowEnquiryPopup(true)
                          );
                          return;
                        }
                        if (token) {
                          downloadBrochure();
                        }
                      }}
                      sx={{
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: '16px',
                        ml: {xxs: 0, md: 1},
                        mt: {xxs: 1.5, md: 0},
                        padding: '5px',
                        px: '14px',
                        width: '168px',
                        height: '33px',
                        display: 'flex',
                        alignItems: 'center',
                        transition: 'background-color 0.3s ease-in-out',
                        '&:hover': {
                          backgroundColor: theme.palette.primary.main,
                          color: '#fff !important',
                          '& img': {
                            filter: 'brightness(0) invert(1)', // Turns image white
                          },
                          '& .text': {
                            color: '#fff', // Changes Typography color
                          },
                        },
                      }}
                      onMouseEnter={() => setHover(true)}
                      onMouseLeave={() => setHover(false)}
                    >
                      <Typography
                        className="text" // Assigning class for better targeting
                        sx={{
                          fontFamily: 'Nunito Sans',
                          fontSize: 14,
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          marginLeft: 1,
                          transition: 'color 0.3s ease-in-out',
                        }}
                      >
                        {!load
                          ? ' Download Brochure'
                          : HiristLoader(getBackgroundColor(hover))}
                      </Typography>
                    </Button>
                  ) : (
                    <></>
                  )}
                </Box>
              )}
              {eq && eq.length > 0 && (
                <Box
                  sx={{
                    color: 'red',
                    backgroundColor: '#fbe3e6',
                    fontSize: 14,
                    borderRadius: '3px',
                    mx: '12px',
                    mt: '16px',
                    py: 1,
                    display: 'flex',
                  }}
                >
                  <Typography mx={'15px'} sx={{fontSize: 14, my: 1}}>
                    It seems you have already enquired for this course. We have
                    shared your interest with the institute. The institute shall
                    contact you shortly
                  </Typography>
                  <span
                    style={{color: 'red', fontSize: '16px', marginRight: '8px'}}
                    onClick={e => {
                      e.preventDefault();

                      e.stopPropagation();
                      seteq('');
                    }}
                  >
                    X
                  </span>
                </Box>
              )}
            </Box>
            <Box
              data-testid="course-tile-stats"
              width={{xxs: 0, md: '35%', lg: '30%', xl: '25%'}}
              sx={{
                cursor: 'pointer',
                display: {xxs: 'none', md: 'flex'},
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'flex-start',
                borderLeft: '1px solid',
                borderColor: '#d9e0eb',
                mt: -1.25,
                mb: -1.25,
                py: 1.25,
                pl: 2.5,
                pr: 1.25,
              }} // Add cursor pointer for better UX
            >
              <Box>
                <Box display={'flex'} alignItems={'center'}>
                  <Image
                    style={{marginRight: 8}}
                    src={IndianRupee}
                    width={12}
                    height={12}
                    alt="rupee"
                  />
                  <Typography sx={{color: 'rgba(0,0,0,.5)', fontSize: 14}}>
                    Fee
                  </Typography>
                </Box>
                <Typography sx={{fontSize: 14, mb: 1.5}}>{data.fee}</Typography>
                <Box display={'flex'} alignItems={'center'}>
                  <Image
                    src={BreifCase}
                    style={{marginRight: 8}}
                    width={12}
                    height={12}
                    alt="rupee"
                  />
                  <Typography sx={{color: 'rgba(0,0,0,.5)', fontSize: 14}}>
                    Experience
                  </Typography>
                </Box>
                <Typography
                  sx={{fontSize: 14, mb: 1.5}}
                >{`${data.minWorkExp} - ${data.maxWorkExp}`}</Typography>
                <Box display={'flex'} alignItems={'center'}>
                  <Image
                    src={OpenBook}
                    width={12}
                    height={12}
                    style={{marginRight: 8}}
                    alt="rupee"
                  />
                  <Typography sx={{color: 'rgba(0,0,0,.5)', fontSize: 14}}>
                    Method
                  </Typography>
                </Box>
                <Typography sx={{fontSize: 14, mb: 1.5}}>
                  {data.deliveryMethod}
                </Typography>
              </Box>
            </Box>
          </Box>
        </JoblistPaper>
      </a>
    </MainComponent>
  );
};
